import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ClipboardCheck, MessageCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { api, formatApiError, useContent } from "@/lib/api";

const QUESTIONS = [
  { id: "eta", label: "Età", type: "text", required: true },
  { id: "altezza", label: "Altezza", type: "text", required: true, placeholder: "Es. 165 cm" },
  { id: "peso", label: "Peso", type: "text", required: true, placeholder: "Es. 60 kg" },
  { id: "lavoro_studio", label: "Lavori o studi?", type: "select", options: ["Lavoro", "Studio", "Entrambi", "Né lavoro né studio"] },
  { id: "tipo_lavoro", label: "Se lavori: il tuo lavoro è sedentario, fisico e/o su turni?", type: "checks", options: ["Sedentario", "Fisico", "Su turni"], note: "Puoi selezionare più opzioni" },
  { id: "obiettivo", label: "Qual è il tuo obiettivo principale?", type: "textarea", required: true, placeholder: "Es. tonificare, aumentare la forza, stare meglio con il mio corpo..." },
  { id: "data_obiettivo", label: "Hai una data entro cui vorresti raggiungerlo?", type: "text", placeholder: "Es. giugno, oppure scrivi 'no'" },
  { id: "allena_ora", label: "Ti alleni attualmente?", type: "select", options: ["Sì", "No"] },
  { id: "da_quanto", label: "Se sì, da quanto?", type: "text", placeholder: "Es. 6 mesi, 2 anni..." },
  { id: "palestra", label: "In quale palestra ti alleni e com'è attrezzata?", type: "textarea", placeholder: "Se ti alleni a casa, descrivi l'attrezzatura che hai a disposizione" },
  { id: "attrezzatura", label: "Hai accesso a:", type: "checks", options: ["Bilanciere", "Rack", "Manubri", "Smith", "Cavi", "Macchinari"], note: "Seleziona tutto ciò che hai a disposizione" },
  { id: "frequenza", label: "Quanti giorni puoi allenarti e per quanto tempo?", type: "textarea", required: true, placeholder: "Es. 3-4 giorni a settimana, circa 1 ora a sessione" },
  { id: "patologie", label: "Hai patologie? Se sì, quali?", type: "textarea", placeholder: "Scrivi 'no' se non ne hai" },
  { id: "farmaci", label: "Assumi farmaci? Se sì, quali?", type: "textarea", placeholder: "Scrivi 'no' se non ne assumi" },
  { id: "infortuni", label: "Hai subito interventi o infortuni? Se sì, di che tipo?", type: "textarea", placeholder: "Scrivi 'no' se non ne hai avuti" },
  { id: "dolori", label: "Hai dolori durante alcuni movimenti?", type: "textarea", placeholder: "Es. durante lo squat o lo stacco... Scrivi 'no' se non ne hai" },
  { id: "pasti", label: "Quanti pasti fai al giorno?", type: "text" },
  { id: "dieta", label: "Segui una dieta?", type: "select", options: ["Sì", "No", "A tratti"] },
  { id: "allergie", label: "Hai allergie o intolleranze? Se sì, a cosa?", type: "textarea", placeholder: "Scrivi 'no' se non ne hai" },
  { id: "sonno", label: "Quante ore dormi?", type: "text", placeholder: "Es. 7 ore" },
  { id: "stress", label: "Quanto ti senti stressata/o, da 1 a 10?", type: "scale" },
  { id: "esercizi_si", label: "Hai degli esercizi che preferisci?", type: "textarea" },
  { id: "esercizi_no", label: "Hai degli esercizi che, invece, non vuoi fare?", type: "textarea" },
];

export default function Onboarding() {
  const content = useContent();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [form, setForm] = useState({ name: "", email: "" });
  const [checks, setChecks] = useState({});
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCheck = (qid, opt) => {
    const cur = checks[qid] || [];
    setChecks({ ...checks, [qid]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const answers = QUESTIONS.map((q) => ({
        question: q.label,
        answer:
          q.type === "checks"
            ? (checks[q.id] || []).join(", ")
            : q.type === "select"
              ? form[q.id] || q.options[0]
              : q.type === "scale"
                ? form[q.id] || "5"
                : form[q.id] || "",
      }));
      await api.post("/onboarding", {
        name: form.name,
        email: form.email,
        session_id: sessionId,
        consent,
        answers,
      });
      setDone(true);
      toast.success("Questionario inviato. Ti aspetto su WhatsApp!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const whatsapp = content.whatsapp_url || "https://wa.me/393248823603";

  return (
    <div data-testid="onboarding-page">
      <Nav />
      <section className="mx-auto max-w-3xl px-6 pb-28 pt-40 lg:pt-52">
        <SectionLabel>Valutazione iniziale</SectionLabel>
        <FadeUp as="h1" delay={0.1} className="mt-6 font-display text-4xl leading-tight text-ivory md:text-6xl" data-testid="onboarding-title">
          Raccontami <span className="italic text-mist">di te.</span>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-8 text-sm leading-loose text-mute md:text-base">
          Questo questionario è il punto di partenza del tuo percorso: con le tue risposte costruisco
          una programmazione davvero su misura. Prenditi qualche minuto e rispondi con sincerità.
        </FadeUp>

        {done ? (
          <FadeUp className="mt-16 flex flex-col items-start gap-6 border border-sage bg-surface p-12" data-testid="onboarding-success">
            <ClipboardCheck size={40} className="text-sage" />
            <p className="font-display text-3xl text-ivory">Questionario inviato.</p>
            <p className="max-w-md text-sm leading-relaxed text-mute">
              Grazie per esserti fidata di me. Prossimi passi: inviami le foto su WhatsApp
              (fronte, lato e retro — stessa luce e stessa distanza) e prenota la tua prima videocall.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="onboarding-whatsapp" className="btn-primary">
                <MessageCircle size={14} /> Scrivimi su WhatsApp
              </a>
              <Link to="/prenota-call" data-testid="onboarding-book-call" className="btn-outline">Prenota la videocall</Link>
            </div>
          </FadeUp>
        ) : (
          <form onSubmit={submit} className="mt-14 flex flex-col gap-8" data-testid="onboarding-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="label-caps mb-3 block text-ivory/60" htmlFor="ob-name">Nome e cognome *</label>
                <input id="ob-name" required minLength={2} className="input-dark" data-testid="onboarding-name"
                  value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="label-caps mb-3 block text-ivory/60" htmlFor="ob-email">Email *</label>
                <input id="ob-email" type="email" required className="input-dark" data-testid="onboarding-email"
                  value={form.email} onChange={set("email")} placeholder="La stessa usata per l'acquisto" />
              </div>
            </div>

            {QUESTIONS.map((q, i) => (
              <FadeUp key={q.id} delay={Math.min(i * 0.03, 0.3)}>
                <label className="label-caps mb-3 block text-ivory/60">
                  {q.label}{q.required ? " *" : ""}
                </label>
                {q.note && <p className="mb-2 text-xs text-mute">{q.note}</p>}
                {q.type === "select" && (
                  <select className="input-dark bg-ink" data-testid={`onboarding-${q.id}`}
                    value={form[q.id] || q.options[0]} onChange={set(q.id)}>
                    {q.options.map((o) => <option key={o}>{o}</option>)}
                  </select>
                )}
                {q.type === "scale" && (
                  <select className="input-dark bg-ink" data-testid={`onboarding-${q.id}`}
                    value={form[q.id] || "5"} onChange={set(q.id)}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}{n === 1 ? " — per nulla" : n === 10 ? " — moltissimo" : ""}</option>)}
                  </select>
                )}
                {q.type === "checks" && (
                  <div className="flex flex-wrap gap-3" data-testid={`onboarding-${q.id}`}>
                    {q.options.map((opt) => {
                      const active = (checks[q.id] || []).includes(opt);
                      return (
                        <button type="button" key={opt} onClick={() => toggleCheck(q.id, opt)}
                          data-testid={`onboarding-${q.id}-${opt.toLowerCase()}`}
                          className={`label-caps rounded-full border px-5 py-2.5 transition-colors duration-300 ${
                            active ? "border-sage bg-sage text-ivory" : "border-line text-mute hover:border-mist/50 hover:text-ivory"
                          }`}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.type === "text" && (
                  <input className="input-dark" required={!!q.required} data-testid={`onboarding-${q.id}`}
                    placeholder={q.placeholder || ""} value={form[q.id] || ""} onChange={set(q.id)} />
                )}
                {q.type === "textarea" && (
                  <textarea rows={3} className="input-dark resize-none" required={!!q.required}
                    data-testid={`onboarding-${q.id}`} placeholder={q.placeholder || ""}
                    value={form[q.id] || ""} onChange={set(q.id)} />
                )}
              </FadeUp>
            ))}

            <FadeUp className="border border-line bg-surface p-8" data-testid="onboarding-photo-note">
              <div className="flex items-start gap-4">
                <Camera size={22} className="mt-1 shrink-0 text-sage" />
                <div>
                  <p className="font-display text-xl text-ivory">Le foto dei progressi</p>
                  <p className="mt-3 text-sm leading-relaxed text-mute">
                    Nella fase successiva ti chiederò delle foto — fronte, lato e retro. Scattale in un posto
                    che abbia sempre la stessa luce e sempre dalla stessa distanza: in questo modo potremo
                    confrontarle man mano e vedere i tuoi progressi nel tempo.
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp>
              <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ivory/90">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  data-testid="onboarding-consent" className="mt-1 h-4 w-4 shrink-0 accent-[#7C2A3E]" />
                Mi farebbe piacere usare foto e feedback come testimonianza del percorso — solo con il tuo consenso.
                Selezionando questa casella acconsenti (facoltativo).
              </label>
            </FadeUp>

            <button type="submit" disabled={sending} data-testid="onboarding-submit" className="btn-primary w-fit disabled:opacity-60">
              {sending ? <Loader2 size={14} className="animate-spin" /> : "Invia il questionario"}
            </button>

            <FadeUp className="border-t border-line pt-8">
              <p className="text-sm leading-relaxed text-mute">
                Ti aspetto su WhatsApp per le foto e per qualsiasi dubbio — e nel frattempo ti ringrazio
                per esserti fidata di me.
              </p>
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" data-testid="onboarding-whatsapp-footer"
                className="label-caps mt-4 flex w-fit items-center gap-2 text-mist transition-colors duration-300 hover:text-ivory">
                <MessageCircle size={14} /> +39 324 882 3603
              </a>
            </FadeUp>
          </form>
        )}
      </section>
      <Footer content={content} />
    </div>
  );
}
